import { CheckCircleIcon, PencilSimpleIcon, TrashIcon, XCircleIcon } from '@phosphor-icons/react';

import type { ActionMenuGroup } from '@/components/common/action-menu';

import type { CustomerRow } from './types';

/**
 * Optional per-action callbacks for the Customer row kebab. Each vertical ticket wires the actions
 * it owns (03 → edit; 04 → enable/disable; 05 → delete); an unset handler keeps its item a no-op.
 */
export type CustomerRowActions = {
  onEdit?: (id: string) => void;
  onEnable?: (row: CustomerRow) => void;
  onDisable?: (row: CustomerRow) => void;
  onDelete?: (row: CustomerRow) => void;
};

/**
 * Status-gated row action menu for the Customer listing.
 *
 * Edit Customer navigates via the injected `onEdit` callback (ticket 03); the status toggle calls
 * `onEnable`/`onDisable` (ticket 04); Delete calls `onDelete`, whose dependency-blocked path is
 * handled by the caller (ticket 05).
 *
 * Gating: Active → Disable Customer · Edit Customer · Delete; Inactive → Enable Customer · Edit
 * Customer · Delete; Activation Pending → Edit Customer · Delete (no toggle).
 */
export function customerRowActionGroups(
  row: CustomerRow,
  actions: CustomerRowActions = {},
): ActionMenuGroup[] {
  const statusItem =
    row.status === 'active'
      ? {
          key: 'disable',
          label: 'Disable Customer',
          icon: <XCircleIcon />,
          onClick: () => actions.onDisable?.(row),
        }
      : row.status === 'inactive'
        ? {
            key: 'enable',
            label: 'Enable Customer',
            icon: <CheckCircleIcon />,
            onClick: () => actions.onEnable?.(row),
          }
        : null; // pending_activation → no enable/disable toggle

  return [
    {
      items: [
        ...(statusItem ? [statusItem] : []),
        {
          key: 'edit',
          label: 'Edit Customer',
          icon: <PencilSimpleIcon />,
          onClick: () => actions.onEdit?.(row.id),
        },
        {
          key: 'delete',
          label: 'Delete',
          icon: <TrashIcon />,
          danger: true,
          onClick: () => actions.onDelete?.(row),
        },
      ],
    },
  ];
}
